import { FC, useEffect } from 'react';
import classNames from 'classnames';
import { ColorField, ImageField } from '@prismicio/client';
import {
  //useFeaturedFlag,
  useProductsByCategory,
  useSelectedStore,
  useShowByLocationSlug,
  useSpacingBetweenProductCard,
} from '@/hooks';
import { useAnalytics } from '@/data';
import {
  Heading,
  Icon,
  Link,
  Product,
  ProductPlaceholder,
  Slider,
} from '@/components';
import {
  EnumCustomerType,
  GProduct,
  SortBySelectOption,
  TreezStore,
} from '@/types';
import { getSlicePadding } from '@/utils';

import style from './products.module.scss';

interface IProductsProps {
  title: string;
  analyticsListName?: string;
  products: GProduct[];
  selectedStore?: TreezStore;
  selectedCustomerType?: EnumCustomerType;
  totalCount?: number;
  seeAllHref?: string;
  seeMoreText?: string;

  // limit and sort
  limit?: number;
  sortBy?: SortBySelectOption | undefined;

  // filters
  filterByStore?: boolean;
  filterByCategory?: string; // filter by category
  filterByBrand?: string; // filter by brand
  filterByGroupId?: string; // filter by group id
  filterByOnSale?: boolean; // filter by on sale
  locationSlug?: string;

  backgroundColor?: ColorField;
  backgroundImage?: ImageField;
  backgroundStyleFixed?: boolean;
  minHeight?: number;

  titleColor?: ColorField;
  seeAllTextColor?: ColorField;
  activeControlColor?: ColorField;
  disableControlColor?: ColorField;

  spacingBottom?: boolean;
  paddingSection?: 'small' | 'medium' | 'large';
}

const Products: FC<IProductsProps> = ({
  title,
  analyticsListName,
  totalCount: _totalCount = 0,
  seeAllHref,
  products: serverProducts,
  selectedStore,
  selectedCustomerType,
  seeMoreText = 'See All',

  limit = 20,
  sortBy,

  filterByStore = false,
  filterByCategory,
  filterByBrand,
  filterByGroupId,
  filterByOnSale,
  locationSlug,

  backgroundColor,
  backgroundImage,
  backgroundStyleFixed,
  minHeight,

  titleColor,
  seeAllTextColor,
  activeControlColor,
  disableControlColor,

  spacingBottom: bottom_spacing = false,
  paddingSection = 'small',
}) => {
  const { measureProductImpressions, measureViewItemsListGA4 } = useAnalytics();
  const {
    breakpoints,
    defaultSlidePerView,
    defaultSpaciBetween,
    spacing_between_card,
    isProductCardLargImage,
  } = useSpacingBetweenProductCard();
  const { store } = useSelectedStore();
  const { showContent } = useShowByLocationSlug(locationSlug);

  useEffect(() => {
    measureProductImpressions(products, analyticsListName as string);
    measureViewItemsListGA4(products, analyticsListName as string);
  }, []);

  const { products, isLoading, total, isError, error } = useProductsByCategory({
    store: store ?? selectedStore,
    customerType: selectedCustomerType,
    sortBy,
    limit,
    products: serverProducts,
    filterByStore,
    filterByBrand,
    filterByCategory,
    filterByGroupId,
    filterByOnSale,
    locationSlug,
  });

  const controlSlideBorder = seeAllTextColor
    ? '1px solid transparent'
    : undefined;
  const background = seeAllTextColor ? 'transparent' : 'initial';

  if (isError) {
    // eslint-disable-next-line no-console
    console.log(error);

    return null;
  }

  if ((!isLoading && !products?.length) || !showContent) {
    return null;
  }

  return (
    <section
      className={classNames(
        style.product__section,
        getSlicePadding(paddingSection),
        {
          ['spacing_bottom']: bottom_spacing,
          ['background__fixed_cover']: backgroundStyleFixed,
          ['background__contained']: !backgroundStyleFixed,
        },
      )}
      style={{
        background: backgroundColor!,
        backgroundImage: backgroundImage?.url
          ? `url(${backgroundImage?.url})`
          : undefined,
        minHeight: `${minHeight || 0}vh`,
      }}
    >
      <Slider
        activatedHover
        breakpoints={breakpoints}
        centeredSlides={false}
        className="products__carousel"
        freeMode
        isNotPaddingMobile={spacing_between_card === 'none'}
        key={store?.shortName}
        seeAllElement={
          <Link
            aria-label={`Go to ${seeAllHref ?? '/deals'}`}
            className={classNames(style.products__see_all)}
            href={seeAllHref ?? '/deals'}
            style={{ color: seeAllTextColor! }}
          >
            {seeMoreText ?? 'SHOP ALL'} {total > 0 && <span>({total})</span>}
          </Link>
        }
        showSliderCardLink={true}
        sliderHeading={
          <Heading level={2} style={{ color: titleColor! }}>
            {title}
          </Heading>
        }
        slidesPerView={defaultSlidePerView}
        spaceBetween={defaultSpaciBetween}
      >
        {isLoading
          ? Array.from({ length: 20 }).map((_, index) => (
              <ProductPlaceholder
                key={index}
                largeCard={isProductCardLargImage}
              />
            ))
          : products.map(product => (
              <Product
                analyticsListName={analyticsListName}
                className={style.slide__main}
                key={product.id}
                product={product}
              />
            ))}
        <Link
          aria-label={`Go to ${seeAllHref ?? '/deals'}`}
          className={classNames(
            style.slide__main,
            style.product__shop_all_slider,
          )}
          href={seeAllHref ?? '/deals'}
          style={{
            color: seeAllTextColor!,
            backgroundColor: background,
          }}
        >
          <Icon name="right" />
          <p
            style={{
              color: seeAllTextColor!,
            }}
          >
            {seeMoreText}
          </p>
        </Link>
      </Slider>
      <style>
        {`.products__carousel {
            .slick-arrow {
              color: ${activeControlColor!};
              border: ${controlSlideBorder};

              &:hover {
                color: ${activeControlColor!};
              }

              &.slick-prev {
                border: ${controlSlideBorder};

                &::after {
                  border-right: ${controlSlideBorder};
                }
              }

              &.slick-disabled {
                color: ${disableControlColor}
              }
            }
          }
        `}
      </style>
    </section>
  );
};

export default Products;
